import { Observable, map } from 'rxjs';
import { GalaxyFilterInputOverride } from '@vendasta/galaxy/filter/chips';
import { SystemFieldIds } from '@galaxy/crm/services/field-service';
import { CRMFilterOverride } from '../../tokens-and-interfaces';
import { IdFilterChipComponent } from './id-filter-chip.component';
import {
  ActivityIdFilterInputComponent,
  CompanyIdFilterInputComponent,
  ContactIdFilterInputComponent,
  CustomObjectIdFilterInputComponent,
  OpportunityIdFilterInputComponent,
} from './id-filter-input.component';

/**
 * Filter input overrides the CRM library applies without the host application asking for them.
 *
 * Only object identity fields are listed. Their values are unique per record, so the default
 * dropdown can never show a useful set and producing it is expensive - listing options for
 * system__contact_id across a multi-location namespace set was taking upwards of fifteen seconds
 * in production. Bounded system fields such as owner and group keep their dropdown, which is both
 * cheap and genuinely useful.
 *
 * Applications can still register their own override for any of these field IDs; host-supplied
 * overrides take precedence over this list.
 */
export const DEFAULT_CRM_FILTER_OVERRIDES: readonly CRMFilterOverride[] = [
  {
    fieldId: SystemFieldIds.ContactID,
    title: 'Contact ID',
    filterInput: ContactIdFilterInputComponent,
    filterChip: IdFilterChipComponent,
  },
  {
    fieldId: SystemFieldIds.CompanyID,
    title: 'Company ID',
    filterInput: CompanyIdFilterInputComponent,
    filterChip: IdFilterChipComponent,
  },
  {
    fieldId: SystemFieldIds.OpportunityID,
    title: 'Opportunity ID',
    filterInput: OpportunityIdFilterInputComponent,
    filterChip: IdFilterChipComponent,
  },
  {
    fieldId: SystemFieldIds.CustomObjectID,
    title: 'Custom object ID',
    filterInput: CustomObjectIdFilterInputComponent,
    filterChip: IdFilterChipComponent,
  },
  {
    fieldId: SystemFieldIds.ActivityID,
    title: 'Activity ID',
    filterInput: ActivityIdFilterInputComponent,
    filterChip: IdFilterChipComponent,
  },
];

/**
 * Resolves the override for a field, preferring one supplied by the host application over the
 * library defaults so an application can always replace the built-in behaviour.
 */
export function resolveFilterOverride(
  fieldId: string,
  hostOverrides: readonly CRMFilterOverride[] | undefined,
): CRMFilterOverride | undefined {
  return (
    hostOverrides?.find((override) => override.fieldId === fieldId) ??
    DEFAULT_CRM_FILTER_OVERRIDES.find((override) => override.fieldId === fieldId)
  );
}

/**
 * Adds the library defaults to a host application's override stream, for the fields the host has
 * not already claimed.
 *
 * A default is dropped outright when the host supplies its own override for that field, rather
 * than relying on ordering to settle the conflict. The two consumers in the chips library resolve
 * overrides differently - the chips component keys them into a Map so the last entry wins, while
 * the edit filter modal takes the first match from Array.find - so any single ordering would give
 * the host precedence in one place and the library default in the other. Emitting one entry per
 * field is the only arrangement both agree on.
 */
export function withDefaultCrmFilterOverrides(
  hostOverrides$: Observable<GalaxyFilterInputOverride[]>,
): Observable<GalaxyFilterInputOverride[]> {
  return hostOverrides$.pipe(
    map((hostOverrides) => [
      ...DEFAULT_CRM_FILTER_OVERRIDES.filter(
        (defaultOverride) => !hostOverrides?.some((hostOverride) => hostOverride.fieldId === defaultOverride.fieldId),
      ),
      ...(hostOverrides ?? []),
    ]),
  );
}
